import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { GraduationCap, X, Loader2 } from "lucide-react";
import axiosClient from "../api/axiosClient";

function renderDescription(text) {
  if (typeof text !== "string") return text;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">
        {part}
      </a>
    ) : (
      part
    )
  );
}

const TutorialModal = ({ open, onClose, section, label, sector = "general", accentColor = "red", description }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const isBlue = accentColor === "blue";

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !section) return;
    setLoading(true);
    setVideoUrl(null);
    const normalizedSector = sector === "peluqueria_unisex" ? "peluquerias" : sector;
    axiosClient
      .get("/tutorials", { params: { section, sector: normalizedSector, lang: "es" } })
      .then((data) => setVideoUrl(data?.videoUrl || null))
      .catch(() => setVideoUrl(null))
      .finally(() => setLoading(false));
  }, [open, section, sector]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={[
          "bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col",
          "max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)]",
          loading || videoUrl ? "max-w-4xl" : "max-w-2xl",
        ].join(" ")}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className={`p-2 rounded-xl shrink-0 ${isBlue ? "bg-blue-50" : "bg-red-50"}`}>
              <GraduationCap size={20} className={isBlue ? "text-blue-600" : "text-red-600"} />
            </div>
            <h2 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{label}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 shrink-0"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {description && (
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50 text-sm text-gray-600 leading-relaxed">
              {typeof description === "string" ? (
                <p>{renderDescription(description)}</p>
              ) : (
                description
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className={`animate-spin ${isBlue ? "text-blue-400" : "text-red-400"}`} />
            </div>
          ) : videoUrl ? (
            <div className="bg-black">
              <video
                key={section}
                src={videoUrl}
                controls
                autoPlay
                style={{ width: "100%", display: "block", aspectRatio: "16/9" }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-6 text-center">
              <div className={`p-5 rounded-full mb-4 ${isBlue ? "bg-blue-50" : "bg-red-50"}`}>
                <GraduationCap size={40} className={isBlue ? "text-blue-400" : "text-red-400"} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Próximamente</h3>
              <p className="text-gray-500 text-sm max-w-sm">
                Estamos preparando este tutorial. ¡Vuelve pronto!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TutorialModal;
